import { useFormContext } from 'react-hook-form'

import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { InputBlock } from 'slab'

import { type Allowance, type Allowances } from './FlatAllowanceExplorer'

type Props = {
  label: string;
  description?: string;
  formIndex: number;
  readOnly?: boolean
  loading?: boolean
  validateEmployeeAllowance?: (value: number, allowance: Allowance) => string | undefined
}

function AllowanceInputBlock ({
  label,
  description,
  formIndex,
  readOnly,
  loading,
  validateEmployeeAllowance,
}: Props) {
  const { register, formState: { errors } } = useFormContext<Allowances>()

  return (
    <InputBlock
      label={label}
      description={description}
    >
      <InputBlock.Row>
        {loading
          ? (
            <SkeletonItem heightClass='h-full' />
            )
          : (
            <InputBlock.Input
              label='Employee allowance'
              type='number'
              min={0}
              readOnly={readOnly || loading}
              {...register(`allowances.${formIndex}.flat_config.employee_amount`, {
                valueAsNumber: true,
                required: 'Employee allowance is required',
                validate: (value, allowances) => {
                  if (value === undefined) {
                    return 'Employee allowance is required'
                  }

                  const allowance = allowances.allowances[formIndex]
                  const customValidate = validateEmployeeAllowance?.(value, allowance)
                  if (customValidate) {
                    return customValidate
                  }

                  if (value <= 0) {
                    return 'Employee allowance must be greater than 0'
                  }
                  return true
                },
              })}
              error={errors.allowances?.[formIndex]?.flat_config?.employee_amount?.message?.toString()}
            />
            )}
      </InputBlock.Row>
      <InputBlock.Row>
        {loading
          ? (
            <SkeletonItem heightClass='h-full' />
            )
          : (
            <>
              <InputBlock.Input
                label='First dependent allowance'
                type='number'
                min={0}
                readOnly={readOnly || loading}
                {...register(`allowances.${formIndex}.flat_config.spouse_amount`, {
                  required: 'First dependent allowance is required',
                  validate: (value) => {
                    if (value === undefined) {
                      return 'First dependent allowance is required'
                    }
                    if (value < 0) {
                      return 'First dependent allowance must be greater than or equal to 0'
                    }
                    return true
                  },
                  setValueAs: (value) => Number(value) || 0,
                })}
                error={errors.allowances?.[formIndex]?.flat_config?.spouse_amount?.message?.toString()}
              />
            </>
            )}
        {loading
          ? (
            <SkeletonItem heightClass='h-full' />
            )
          : (
            <InputBlock.Input
              label='Additional dependent(s) allowance'
              type='number'
              min={0}
              readOnly={readOnly || loading}
              {...register(`allowances.${formIndex}.flat_config.dependent_amount`, {
                required: 'Additional dependent(s) allowance is required',
                validate: (value) => {
                  if (value === undefined) {
                    return 'Additional dependent(s) allowance is required'
                  }
                  if (value < 0) {
                    return 'Additional dependent(s) allowance must be greater than or equal to 0'
                  }
                  return true
                },
                setValueAs: (value) => Number(value) || 0,
              })}
              error={errors.allowances?.[formIndex]?.flat_config?.dependent_amount?.message?.toString()}
            />
            )}
      </InputBlock.Row>
    </InputBlock>
  )
}

export default AllowanceInputBlock
